import { createContext, useContext, ReactNode, useReducer } from "react"
import { CalendarEvent } from "toptaal-shared"
import { useQueryClient } from "@tanstack/react-query"
import {
  scheduleQueryKeys,
  useRegisterStudentAbsentForEventMutation,
  useRemoveStudentFromIntakeMutation,
  userQueryKeys,
  canCancelEvent,
  canCheckInToEvent,
} from "students-api"
import { eventModalReducer, initialState } from "./EventModalReducer"

interface EventModalContextValue {
  isEventModalOpen: boolean
  openEventModal: (event: CalendarEvent) => void
  closeEventModal: () => void

  isAbsentConfirmModalOpen: boolean
  showAbsentConfirmModal: () => void
  closeAbsentConfirmModal: () => void

  isRegisterModalOpen: boolean
  handleRegisterModalOpen: () => void
  handleRegisterModalClose: () => void

  handleAbsent: (reason?: string, reasonOther?: string) => Promise<void>

  selectedEvent: CalendarEvent | null
  setSelectedEvent: (event: CalendarEvent) => void

  canCheckIn: boolean
  canCancel: boolean
  hasActions: boolean
  isPresent: boolean

  isFinished: boolean
}

const EventModalContext = createContext<EventModalContextValue | undefined>(
  undefined,
)

export function EventModalProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(eventModalReducer, initialState)

  const { selectedEvent } = state

  const isFinished = selectedEvent
    ? new Date(selectedEvent.end) < new Date()
    : false

  const removeStudentFromIntakeMutation = useRemoveStudentFromIntakeMutation({
    intakeId: Number(state.selectedEvent?.id),
  })

  const registerStudentAbsentMutation =
    useRegisterStudentAbsentForEventMutation({
      entityType: "lesson",
      entityId: state.selectedEvent?.id?.toString() ?? "",
    })

  const handleAbsentForIntake = async () => {
    await removeStudentFromIntakeMutation.mutateAsync(undefined, {
      onSuccess: () => {
        dispatch({ type: "RESET" })

        queryClient.invalidateQueries({
          queryKey: userQueryKeys.current(),
        })

        queryClient.invalidateQueries({
          queryKey: scheduleQueryKeys.all,
        })
      },
    })
  }

  const handleAbsentForLesson = async (
    reason: string,
    reasonOther?: string,
  ) => {
    await registerStudentAbsentMutation.mutateAsync(
      { absenceReason: reason, reasonOther },
      {
        onSuccess: () => {
          dispatch({ type: "RESET" })
          queryClient.invalidateQueries({
            queryKey: userQueryKeys.current(),
          })
          queryClient.invalidateQueries({
            queryKey: scheduleQueryKeys.all,
          })
        },
      },
    )
  }

  const handleAbsent = async (reason?: string, reasonOther?: string) => {
    if (selectedEvent?.type === "lesson" && reason) {
      return await handleAbsentForLesson(reason, reasonOther)
    } else if (selectedEvent?.type === "intake") {
      return await handleAbsentForIntake()
    }
  }

  const openEventModal = (event: CalendarEvent) => {
    dispatch({ type: "OPEN_EVENT_MODAL", payload: event })
  }

  const closeEventModal = () => {
    dispatch({ type: "CLOSE_EVENT_MODAL" })
  }

  const showAbsentConfirmModal = () => {
    dispatch({ type: "OPEN_CONFIRM_MODAL" })
  }

  const closeAbsentConfirmModal = () => {
    dispatch({ type: "CLOSE_CONFIRM_MODAL" })
  }

  const handleRegisterModalOpen = () => {
    dispatch({ type: "OPEN_REGISTER_MODAL" })
  }

  const handleRegisterModalClose = () => {
    dispatch({ type: "CLOSE_REGISTER_MODAL" })
  }

  const setSelectedEvent = (event: CalendarEvent) => {
    dispatch({ type: "SET_SELECTED_EVENT", payload: event })
  }

  const value = {
    isEventModalOpen: state.isEventModalOpen,
    isAbsentConfirmModalOpen: state.isAbsentConfirmModalOpen,
    selectedEvent: state.selectedEvent,
    setSelectedEvent,
    isRegisterModalOpen: state.isRegisterModalOpen,
    handleRegisterModalOpen,
    handleRegisterModalClose,
    openEventModal,
    closeEventModal,
    showAbsentConfirmModal,
    closeAbsentConfirmModal,
    handleAbsent,
    canCheckIn: canCheckInToEvent(selectedEvent),
    canCancel: canCancelEvent(selectedEvent),
    hasActions:
      canCheckInToEvent(selectedEvent) || canCancelEvent(selectedEvent),
    isPresent: selectedEvent?.studentStatus === "present",
    isFinished,
  }

  return (
    <EventModalContext.Provider value={value}>
      {children}
    </EventModalContext.Provider>
  )
}

export function useEventModalContext() {
  const context = useContext(EventModalContext)
  if (context === undefined) {
    throw new Error(
      "useEventModalContext must be used within an EventModalProvider",
    )
  }

  return context
}
